// MOD5 CUSTOM START [PRODUCT-001]: Scoped product option/property lifecycle
if (!customElements.get('mod5-product-option-selector')) {
  customElements.define(
    'mod5-product-option-selector',
    class Mod5ProductOptionSelector extends HTMLElement {
      constructor() {
        super();

        this.boundAddToCartButtons = new Set();
        this.validatedAddToCartClickEvents = new WeakSet();
        this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
        this.handleScopedEvent = this.handleScopedEvent.bind(this);
        this.handlePageShow = this.handlePageShow.bind(this);
        this.handlePlacedFieldChange = this.handlePlacedFieldChange.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleVariantPickerMutations = this.handleVariantPickerMutations.bind(this);
      }

      connectedCallback() {
        if (this.controllerConnected) return;

        this.controllerConnected = true;
        this.productInfo = this.closest('product-info');
        this.eventRoot = this.productInfo || this;
        this.initializeOwnedFields();
        this.initializeTextFieldPlacement();

        this.eventRoot.addEventListener('change', this.handleScopedEvent);
        this.eventRoot.addEventListener('input', this.handleScopedEvent);
        this.eventRoot.addEventListener('click', this.handleAddToCartClick, true);
        window.addEventListener('pageshow', this.handlePageShow);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
        this.observeVariantPicker();

        this.updateComponentState();
        this.scheduleUpdate();
      }

      disconnectedCallback() {
        if (!this.controllerConnected) return;

        this.eventRoot?.removeEventListener('change', this.handleScopedEvent);
        this.eventRoot?.removeEventListener('input', this.handleScopedEvent);
        this.eventRoot?.removeEventListener('click', this.handleAddToCartClick, true);
        window.removeEventListener('pageshow', this.handlePageShow);
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        this.owningProductForm?.removeEventListener('submit', this.handleSubmit, true);
        this.clearAddToCartButtonBindings();
        this.variantPickerObserver?.disconnect();

        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);

        this.restoreTextFields(true);
        this.updateFrame = null;
        this.eventRoot = null;
        this.productInfo = null;
        this.owningProductForm = null;
        this.owningProductFormId = null;
        this.customFields = null;
        this.grainField = null;
        this.placementConfigs = null;
        this.placementFields = null;
        this.variantPickerObserver = null;
        this.controllerConnected = false;
      }

      handleScopedEvent(event) {
        const variantPicker = this.variantPicker;
        const isComponentEvent = this.contains(event.target);
        const isVariantPickerEvent = variantPicker?.contains(event.target);

        if (!isComponentEvent && !isVariantPickerEvent) return;

        if (event.type === 'input' && event.target === this.getGrainInput()) {
          this.clearGrainWarning();
        }

        if (event.type === 'change' && event.target === this.getGrainConfirmation()) {
          this.clearGrainWarning(true);
        }

        this.scheduleUpdate();
      }

      handlePageShow() {
        this.scheduleUpdate();
      }

      handlePlacedFieldChange(event) {
        // These are line-item properties, so their changes must not reach Dawn's variant-selects handler.
        if (event.target === this.getGrainInput()) this.clearGrainWarning();
        if (event.target === this.getGrainConfirmation()) this.clearGrainWarning(true);
        if (this.variantPicker?.contains(event.currentTarget)) event.stopPropagation();
        this.scheduleUpdate();
      }

      handleSectionLoad(event) {
        if (event.target instanceof Element && event.target.contains(this)) {
          this.scheduleUpdate();
        }
      }

      handleVariantPickerMutations(mutations) {
        // Retry the form binding for any scoped subtree change because Dawn can render or replace it later.
        this.connectProductForm();

        const variantPickerChanged = mutations.some((mutation) =>
          [...mutation.addedNodes, ...mutation.removedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches('variant-selects') || node.querySelector('variant-selects'))
          )
        );

        if (variantPickerChanged) this.scheduleUpdate();
      }

      scheduleUpdate() {
        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);

        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateComponentState();
        });
      }

      updateComponentState() {
        this.connectProductForm();
        this.placeTextFields();
        this.updateFieldVisibility();
        this.clearResolvedGrainWarning();
        this.clearResolvedErrors();
        this.refreshErrorSummary();
      }

      initializeOwnedFields() {
        this.customFields = new Map(
          Array.from(this.querySelectorAll('[data-mod5-custom-field]')).map((field) => [
            field.dataset.mod5CustomField,
            field,
          ])
        );
        this.grainField = this.querySelector('[data-mod5-grain-field]');
      }

      initializeTextFieldPlacement() {
        const labelMode = this.dataset.mod5LabelMode;
        const configs = [];

        if (labelMode === 'pistol_rifle' || labelMode === 'compact' || labelMode === 'shotgun') {
          configs.push(this.createPlacementConfig('caliber', 'Caliber', this.getCustomField('caliber')));
        }

        if (labelMode === 'pistol_rifle' || labelMode === 'shotgun') {
          configs.push(this.createPlacementConfig('type', 'Type', this.getCustomField('type')));
        }

        if (labelMode === 'pistol_rifle') {
          configs.push(this.createPlacementConfig('grain-weight', 'Text Color', this.grainField, 'before'));
        }

        if (labelMode === 'shotgun') {
          configs.push(
            this.createPlacementConfig(
              'type-line-3',
              'Text Color',
              this.querySelector('[data-mod5-property-group="type-length-line2"]'),
              'before'
            ),
            this.createPlacementConfig(
              'custom-type-line-3',
              'Text Color',
              this.getCustomField('type-length-line2'),
              'before'
            ),
            this.createPlacementConfig(
              'length-shot-size-line-4',
              'Text Color',
              this.querySelector('[data-mod5-property-group="length-shot-size"]'),
              'before'
            ),
            this.createPlacementConfig(
              'custom-length-shot-size-line-4',
              'Text Color',
              this.getCustomField('length-shot-size'),
              'before'
            )
          );
        }

        this.placementConfigs = configs.filter(Boolean);
        this.placementFields = new Set(this.placementConfigs.map(({ field }) => field));
      }

      createPlacementConfig(key, optionName, field, position = 'after') {
        if (!field) return null;

        const anchor = document.createComment(`Mod-5 ${key} field position`);
        field.before(anchor);
        field.addEventListener('change', this.handlePlacedFieldChange);

        const stableAttributes = Array.from(field.querySelectorAll('[id], [form]')).map((element) => ({
          element,
          id: element.getAttribute('id'),
          form: element.getAttribute('form'),
        }));

        return { key, optionName, field, position, anchor, stableAttributes };
      }

      placeTextFields() {
        if (!this.placementConfigs) return;

        const beforeConfigs = this.placementConfigs.filter(({ position }) => position === 'before').reverse();
        const orderedConfigs = [
          ...this.placementConfigs.filter(({ position }) => position !== 'before'),
          ...beforeConfigs,
        ];
        const beforeInsertionPoints = new Map();

        orderedConfigs.forEach((config) => {
          const optionGroup = this.findVariantOptionGroup(config.optionName);

          if (!optionGroup) {
            this.restoreTextField(config);
            return;
          }

          this.restoreStableAttributes(config);
          config.field.dataset.mod5PlacedTextField = config.key;

          if (config.position === 'before') {
            const insertionPoint = beforeInsertionPoints.get(optionGroup) || optionGroup;
            if (insertionPoint.previousElementSibling !== config.field) insertionPoint.before(config.field);
            beforeInsertionPoints.set(optionGroup, config.field);
          } else if (optionGroup.nextElementSibling !== config.field) {
            optionGroup.after(config.field);
          }
        });
      }

      restoreTextField(config) {
        this.restoreStableAttributes(config);
        delete config.field.dataset.mod5PlacedTextField;
        if (config.anchor.nextSibling !== config.field) config.anchor.after(config.field);
      }

      restoreTextFields(removeListeners = false) {
        this.placementConfigs?.forEach((config) => {
          this.restoreTextField(config);

          if (removeListeners) {
            config.field.removeEventListener('change', this.handlePlacedFieldChange);
            config.anchor.remove();
          }
        });
      }

      restoreStableAttributes(config) {
        config.stableAttributes.forEach(({ element, id, form }) => {
          if (id === null) {
            element.removeAttribute('id');
          } else {
            element.setAttribute('id', id);
          }

          if (form === null) {
            element.removeAttribute('form');
          } else {
            element.setAttribute('form', form);
          }
        });
      }

      findVariantOptionGroup(optionName) {
        const variantPicker = this.variantPicker;
        if (!variantPicker) return null;

        const normalizedOptionName = this.normalizeText(optionName);
        const groups = Array.from(variantPicker.children).filter(
          (element) => element.matches('.product-form__input') && !this.placementFields?.has(element)
        );
        const dataNameMatch = groups.find((group) =>
          Array.from(group.querySelectorAll('[data-option-name]')).some((control) =>
            this.normalizeText(control.dataset.optionName).includes(normalizedOptionName)
          )
        );

        if (dataNameMatch) return dataNameMatch;

        return (
          groups.find((group) => {
            const legend = group.querySelector('legend');
            const label = group.querySelector('.form__label, label');
            return this.normalizeText(legend?.textContent || label?.textContent).includes(normalizedOptionName);
          }) || null
        );
      }

      observeVariantPicker() {
        if (!this.productInfo || this.variantPickerObserver) return;

        this.variantPickerObserver = new MutationObserver(this.handleVariantPickerMutations);
        this.variantPickerObserver.observe(this.productInfo, { childList: true, subtree: true });
      }

      updateFieldVisibility() {
        const caliber = this.getSelectedVariantOptionValue('Caliber');
        const type = this.getSelectedVariantOptionValue('Type');
        const typeLengthLine2 = this.getSelectedPropertyValue('Type - Line #3');
        const lengthShotSize = this.getSelectedPropertyValue('Length/Shot Size - Line #4');
        const textLines = this.getSelectedPropertyValue('Text Lines');

        this.setCustomFieldVisibility('caliber', this.isCustomValue(caliber));
        this.setCustomFieldVisibility('type', this.isCustomValue(type));
        this.setCustomFieldVisibility('type-length-line2', this.isCustomValue(typeLengthLine2));
        this.setCustomFieldVisibility('length-shot-size', this.isCustomValue(lengthShotSize));
        this.setCustomFieldVisibility('text-line-2', textLines?.trim().toLowerCase() === '2-lines');
      }

      handleAddToCartClick(event) {
        if (!(event.target instanceof Element)) return;

        const submitButton = event.target.closest(
          "button[type='submit'][name='add'], input[type='submit'][name='add']"
        );
        if (!submitButton || submitButton.disabled || !this.eventRoot?.contains(submitButton)) return;

        // Product-info can replace the product form during variant updates; resolve it again before matching.
        this.connectProductForm();
        if (!this.owningProductForm || this.asFormElement(submitButton) !== this.owningProductForm) return;
        if (this.validatedAddToCartClickEvents.has(event)) return;

        this.validatedAddToCartClickEvents.add(event);
        this.validateBeforeAddToCart(event);
      }

      handleSubmit(event) {
        if (event.target !== this.owningProductForm) return;

        this.validateBeforeAddToCart(event);
      }

      validateBeforeAddToCart(event) {
        this.updateFieldVisibility();
        this.clearResolvedGrainWarning();

        const errors = this.getValidationErrors();
        const grainWarningRequired = this.isGrainWarningRequired();

        this.clearAllFieldErrors();

        if (errors.length === 0 && !grainWarningRequired) {
          this.hideErrorSummary();
          return true;
        }

        event.preventDefault();
        event.stopImmediatePropagation?.();

        errors.forEach((error) => this.showFieldError(error));
        if (grainWarningRequired) this.showGrainWarning();

        this.showErrorSummary(errors.length + Number(grainWarningRequired), grainWarningRequired);
        const firstInvalidInput = errors[0]?.input || this.getGrainInput();
        if (this.isFieldAvailable(firstInvalidInput)) firstInvalidInput.focus();

        return false;
      }

      getValidationErrors() {
        const errors = [];
        const caliber = this.getSelectedVariantOptionValue('Caliber');
        const type = this.getSelectedVariantOptionValue('Type');
        const typeLengthLine2 = this.getSelectedPropertyValue('Type - Line #3');
        const lengthShotSize = this.getSelectedPropertyValue('Length/Shot Size - Line #4');
        const textLines = this.getSelectedPropertyValue('Text Lines')?.trim().toLowerCase();

        if (this.isCustomValue(caliber)) {
          this.addBlankFieldError(errors, 'caliber', 'Enter a custom caliber.');
        }

        if (this.isCustomValue(type)) {
          this.addBlankFieldError(errors, 'type', 'Enter a custom type.');
        }

        if (this.isCustomValue(typeLengthLine2)) {
          this.addBlankFieldError(errors, 'type-length-line2', 'Enter a custom Type - Line #3 value.');
        }

        if (this.isCustomValue(lengthShotSize)) {
          this.addBlankFieldError(errors, 'length-shot-size', 'Enter a custom Length/Shot Size - Line #4 value.');
        }

        if (textLines === '1-line' || textLines === '2-lines') {
          this.addBlankFieldError(errors, 'text-line-1', 'Enter Custom Text Line #1.');
        }

        if (textLines === '2-lines') {
          this.addBlankFieldError(errors, 'text-line-2', 'Enter Custom Text Line #2.');
        }

        return errors;
      }

      addBlankFieldError(errors, fieldName, message) {
        const input = this.getCustomFieldInput(fieldName);
        if (!this.isFieldAvailable(input) || input.value.trim() !== '') return;

        errors.push({ fieldName, input, message });
      }

      getSelectedVariantOptionValue(optionName) {
        const variantPicker = this.variantPicker;
        if (!variantPicker) return null;

        const normalizedOptionName = this.normalizeText(optionName);
        const selectedControls = variantPicker.querySelectorAll("input[type='radio']:checked, select");
        const selectedControl = Array.from(selectedControls).find(
          (control) =>
            !this.placementFields?.has(control.closest('.product-form__input')) &&
            this.getVariantOptionName(control).includes(normalizedOptionName)
        );

        return selectedControl?.value || null;
      }

      getVariantOptionName(control) {
        const selectedOption = control.matches('select') ? control.selectedOptions[0] : control;
        const dataOptionName = selectedOption?.dataset.optionName || control.dataset.optionName;

        if (dataOptionName) return this.normalizeText(dataOptionName);

        // Dawn groups pills with a legend and dropdowns with a label.
        const fieldsetLegend = control.closest('fieldset')?.querySelector('legend');
        const groupLabel = control.closest('.product-form__input')?.querySelector('.form__label, label');

        return this.normalizeText(fieldsetLegend?.textContent || groupLabel?.textContent);
      }

      getSelectedPropertyValue(propertyName) {
        const expectedName = `properties[${propertyName}]`;
        const selectedProperty = Array.from(this.eventRoot.querySelectorAll("input[type='radio']:checked")).find(
          (input) => input.name === expectedName
        );

        return selectedProperty?.value || null;
      }

      setCustomFieldVisibility(fieldName, shouldShow) {
        const field = this.getCustomField(fieldName);
        if (!field) return;

        const input = field.querySelector('[data-mod5-custom-input]');
        field.hidden = !shouldShow;

        if (!input) return;

        input.disabled = !shouldShow;
        if (!shouldShow) {
          input.value = '';
          this.clearFieldError(fieldName);
        }
      }

      getCustomFieldInput(fieldName) {
        return this.getCustomField(fieldName)?.querySelector('[data-mod5-custom-input]') || null;
      }

      getCustomField(fieldName) {
        return this.customFields?.get(fieldName) || null;
      }

      getGrainInput() {
        return this.grainField?.querySelector('[data-mod5-grain-input]') || null;
      }

      getGrainWarning() {
        return this.grainField?.querySelector('[data-mod5-grain-warning]') || null;
      }

      getGrainConfirmation() {
        return this.grainField?.querySelector('[data-mod5-grain-confirmation]') || null;
      }

      isFieldAvailable(input) {
        return Boolean(input && !input.disabled && !input.hidden && !input.closest('[hidden]'));
      }

      isGrainWarningRequired() {
        const input = this.getGrainInput();
        const confirmation = this.getGrainConfirmation();

        return Boolean(
          this.isFieldAvailable(input) && input.value.trim() === '' && confirmation && !confirmation.checked
        );
      }

      showGrainWarning() {
        const input = this.getGrainInput();
        const warning = this.getGrainWarning();
        if (!input || !warning) return;

        warning.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        this.addDescribedBy(input, warning.dataset.mod5GrainWarningMessageId);
      }

      clearGrainWarning(preserveConfirmation = false) {
        const input = this.getGrainInput();
        const warning = this.getGrainWarning();
        const confirmation = this.getGrainConfirmation();

        if (input && warning) {
          input.removeAttribute('aria-invalid');
          this.removeDescribedBy(input, warning.dataset.mod5GrainWarningMessageId);
          warning.hidden = true;
        }

        if (confirmation && !preserveConfirmation) confirmation.checked = false;
      }

      clearResolvedGrainWarning() {
        const input = this.getGrainInput();
        const confirmation = this.getGrainConfirmation();

        if (!this.isFieldAvailable(input) || input.value.trim() !== '') {
          this.clearGrainWarning();
        } else if (confirmation?.checked) {
          this.clearGrainWarning(true);
        }
      }

      showFieldError({ fieldName, input, message }) {
        const field = this.getCustomField(fieldName);
        const error = field?.querySelector('[data-mod5-field-error]');
        if (!field || !error) return;

        error.textContent = message;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        this.addDescribedBy(input, error.id);
      }

      clearFieldError(fieldName) {
        const field = this.getCustomField(fieldName);
        const input = field?.querySelector('[data-mod5-custom-input]');
        const error = field?.querySelector('[data-mod5-field-error]');
        if (!field || !input || !error) return;

        input.removeAttribute('aria-invalid');
        this.removeDescribedBy(input, error.id);
        error.textContent = '';
        error.hidden = true;
      }

      clearAllFieldErrors() {
        this.customFields?.forEach((field) => {
          this.clearFieldError(field.dataset.mod5CustomField);
        });
      }

      clearResolvedErrors() {
        const unresolvedFields = new Set(this.getValidationErrors().map((error) => error.fieldName));

        this.customFields?.forEach((field, fieldName) => {
          const input = field.querySelector('[data-mod5-custom-input][aria-invalid="true"]');
          if (input && !unresolvedFields.has(fieldName)) this.clearFieldError(fieldName);
        });
      }

      addDescribedBy(input, errorId) {
        const describedBy = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        describedBy.add(errorId);
        input.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      }

      removeDescribedBy(input, errorId) {
        const describedBy = (input.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter((id) => id && id !== errorId);

        if (describedBy.length > 0) {
          input.setAttribute('aria-describedby', describedBy.join(' '));
        } else {
          input.removeAttribute('aria-describedby');
        }
      }

      showErrorSummary(issueCount, includesGrainWarning = false) {
        const summary = this.querySelector('[data-mod5-error-summary]');
        if (!summary) return;

        if (includesGrainWarning) {
          summary.textContent =
            'Please review the highlighted product options before adding this item to your cart.';
        } else {
          summary.textContent =
            issueCount === 1
              ? 'Please correct the highlighted product option before adding this item to your cart.'
              : 'Please correct the highlighted product options before adding this item to your cart.';
        }
        summary.hidden = false;
      }

      hideErrorSummary() {
        const summary = this.querySelector('[data-mod5-error-summary]');
        if (!summary) return;

        summary.textContent = '';
        summary.hidden = true;
      }

      refreshErrorSummary() {
        const visibleErrorCount = Array.from(this.customFields?.values() || []).filter((field) => {
          const error = field.querySelector('[data-mod5-field-error]');
          return error && !error.hidden;
        }).length;
        const grainWarningVisible = this.getGrainWarning()?.hidden === false;

        if (visibleErrorCount > 0 || grainWarningVisible) {
          this.showErrorSummary(visibleErrorCount + Number(grainWarningVisible), grainWarningVisible);
        } else {
          this.hideErrorSummary();
        }
      }

      connectProductForm() {
        const nextProductForm = this.asFormElement(this.resolveOwningProductForm());
        if (nextProductForm === this.owningProductForm) {
          this.owningProductFormId = nextProductForm?.getAttribute('id') || null;
          this.refreshAddToCartButtonBindings();
          return;
        }

        this.owningProductForm?.removeEventListener('submit', this.handleSubmit, true);
        this.owningProductForm = nextProductForm;
        this.owningProductFormId = nextProductForm?.getAttribute('id') || null;
        this.owningProductForm?.addEventListener('submit', this.handleSubmit, true);
        this.refreshAddToCartButtonBindings();
      }

      refreshAddToCartButtonBindings() {
        const scope = this.productInfo || this.eventRoot;
        const nextButtons = new Set();

        if (scope && this.owningProductForm) {
          scope.querySelectorAll("button[type='submit'], input[type='submit']").forEach((button) => {
            if (button.getAttribute('name') !== 'add') return;
            if (this.asFormElement(button) !== this.owningProductForm) return;

            nextButtons.add(button);
          });
        }

        this.boundAddToCartButtons.forEach((button) => {
          if (!nextButtons.has(button)) button.removeEventListener('click', this.handleAddToCartClick, true);
        });

        nextButtons.forEach((button) => {
          if (!this.boundAddToCartButtons.has(button)) {
            button.addEventListener('click', this.handleAddToCartClick, true);
          }
        });

        this.boundAddToCartButtons = nextButtons;
      }

      clearAddToCartButtonBindings() {
        this.boundAddToCartButtons.forEach((button) => {
          button.removeEventListener('click', this.handleAddToCartClick, true);
        });
        this.boundAddToCartButtons.clear();
      }

      resolveOwningProductForm() {
        const scope = this.productInfo || this.eventRoot;
        if (!scope) return null;

        const scopedForms = Array.from(scope.querySelectorAll('form'))
          .map((candidate) => this.asFormElement(candidate))
          .filter(Boolean);
        const configuredFormId = this.dataset.productFormId?.trim();

        if (configuredFormId) {
          const configuredForm = scopedForms.find((form) => form.getAttribute('id') === configuredFormId);
          if (configuredForm) return configuredForm;
        }

        const formReferenceInputs = [
          ...Array.from(this.customFields?.values() || []).map((field) =>
            field.querySelector('[data-mod5-custom-input]')
          ),
          this.getGrainInput(),
        ].filter(Boolean);

        for (const input of formReferenceInputs) {
          const referencedFormId = input.getAttribute('form')?.trim();
          const referencedScopedForm = referencedFormId
            ? scopedForms.find((form) => form.getAttribute('id') === referencedFormId)
            : null;
          const associatedForm = this.asFormElement(input);

          if (referencedScopedForm) return referencedScopedForm;
          if (this.isFormInOwningProductContext(associatedForm)) return associatedForm;
        }

        const addToCartForm = this.asFormElement(scope.querySelector("form[data-type='add-to-cart-form']"));
        if (addToCartForm) return addToCartForm;

        const dawnProductForms = Array.from(scope.querySelectorAll('product-form'));
        for (const productForm of dawnProductForms) {
          const nestedForm = this.asFormElement(productForm.querySelector('form'));
          if (nestedForm) return nestedForm;
        }

        return null;
      }

      asFormElement(candidate) {
        if (candidate instanceof HTMLFormElement) return candidate;

        if (
          (candidate instanceof HTMLInputElement || candidate instanceof HTMLButtonElement) &&
          candidate.form instanceof HTMLFormElement
        ) {
          return candidate.form;
        }

        return null;
      }

      isFormInOwningProductContext(candidate) {
        const form = this.asFormElement(candidate);
        if (!form) return false;
        if (!this.productInfo) return true;

        const formProductInfo = form.closest('product-info');
        return !formProductInfo || formProductInfo === this.productInfo;
      }

      isCustomValue(value) {
        return this.normalizeText(value) === 'custom';
      }

      normalizeText(value) {
        return value?.replace(/\s+/g, ' ').trim().toLowerCase() || '';
      }

      get variantPicker() {
        if (!this.productInfo) return null;

        const sectionId = this.dataset.sectionId;
        return (
          Array.from(this.productInfo.querySelectorAll('variant-selects')).find(
            (variantPicker) => variantPicker.dataset.section === sectionId
          ) || null
        );
      }
    }
  );
}
// MOD5 CUSTOM END [PRODUCT-001]
